/**
 * The figures a county's landing page lists under its headline.
 *
 * Written once here because two things draw them: the React page an ad lands
 * on, and the HTML a crawler reads before any script runs
 * (`publicPages.ts`, refreshed in the web container by
 * `scripts/publish-pages.ts`). Both must say the same thing about the same
 * county, so neither spells out a label of its own.
 *
 * The rule is the landing page's, unchanged: a metric outside the county's
 * published signals is not zero there — it is unpublished, and the tile is
 * dropped rather than shown as a zero that reads as "none found". The building
 * figures appear only where the county rolls up buildings at all.
 */

import type { HeadlineStats } from "../api/types";

export interface Metric {
  label: string;
  /** Undefined while the figures are still loading; the page shows a dash. */
  value: number | undefined;
}

/**
 * `stats` absent means "not loaded yet": the tiles a county might carry are
 * listed with no value, and the building tiles wait, as they always have.
 * `signals` absent means every signal, for the same reason `headline` says so.
 */
export function landingMetrics(
  stats: HeadlineStats | null | undefined,
  signals?: string[],
): Metric[] {
  const publishes = (flag: string) => (signals ? signals.includes(flag) : true);
  const metrics: Metric[] = [];
  if (stats && stats.buildings > 0) {
    metrics.push({ label: "Buildings scored", value: stats.buildings });
    metrics.push({ label: "Milestone overdue", value: stats.overdue });
  }
  if (publishes("unsafe_structure")) metrics.push({ label: "Unsafe structure", value: stats?.unsafe });
  if (publishes("lien_recorded")) metrics.push({ label: "Violation liens", value: stats?.liens });
  if (publishes("homestead_lost")) metrics.push({ label: "Homestead dropped", value: stats?.homestead_lost });
  metrics.push({ label: "Parcels scored", value: stats?.units });
  return metrics;
}
